<template>
    <div class="policy-wrapper p-4 md:p-6 surface-card border-round-xl shadow-1 text-left">
        <div class="mb-5">
            <p class="text-900 font-bold mb-2">Version: 1.0</p>
            <p class="text-900 font-bold mb-2">Jurisdiction: Democratic Socialist Republic of Sri Lanka</p>
            <p class="text-600 italic font-light">
                These Terms take effect from the date you access, browse, register an account on, or otherwise use the Platform or its services.
            </p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">1. INTRODUCTION</h2>
            <p class="text-body mb-2">These Terms and Conditions (“Terms”) govern the access to and use of the ILearn’d Tutors platform (“Platform”) by students and, where applicable, their parents or legal guardians.</p>
            <p class="text-body">By registering, booking a session, or using the Platform, the student (and parent or guardian, where applicable) agrees to be legally bound by these Terms.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">2. DEFINITIONS</h2>
            <ul class="text-body list-none p-0 m-0">
                <li class="mb-1">(a) <b>“Platform”</b> means the ILearn’d Tutors digital platform and related services.</li>
                <li class="mb-1">(b) <b>“Student”</b> means a learner registered on the Platform.</li>
                <li class="mb-1">(c) <b>“Tutor”</b> means an independent educator providing tutoring services through the Platform.</li>
                <li class="mb-1">(d) <b>“Session”</b> means a scheduled tutoring class conducted through the Platform.</li>
                <li class="mb-1">(e) <b>“Past Papers”</b> means official or verified examination papers, marking schemes, and related academic materials.</li>
                <li class="mb-1">(f) <b>“AI Features”</b> means artificial intelligence–enabled tools provided by the Platform to support learning and administration.</li>
                <li>(g) <b>“Minor”</b> means a student under eighteen (18) years of age.</li>
            </ul>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">3. ELIGIBILITY AND ACCOUNT REGISTRATION</h2>
            <p class="text-body mb-2">3.1 Students may register on the Platform subject to verification requirements.</p>
            <p class="text-body mb-2">3.2 Where the student is a Minor, <b>parental or legal guardian consent is mandatory</b> prior to account activation.</p>
            <p class="text-body">3.3 Students are responsible for maintaining accurate and up-to-date account information.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">4. ROLE OF ILEARN’D TUTORS</h2>
            <p class="text-body mb-2">4.1 ILearn’d Tutors operates solely as a <b>technology platform</b> facilitating access to tutors, scheduling tools, learning resources, analytics, and secure payment processing.</p>
            <p class="text-body mb-2">4.2 ILearn’d Tutors does not:</p>
            <ul class="text-body list-none p-0 m-0 ml-3">
                <li class="mb-1">(a) employ tutors;</li>
                <li class="mb-1">(b) manage or control academic curricula;</li>
                <li>(c) guarantee grades, results, or academic outcomes.</li>
            </ul>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">5. MODE OF SESSIONS (ONLINE-ONLY POLICY)</h2>
            <p class="text-body mb-2">5.1 All tutoring sessions facilitated through the Platform shall be conducted <b>exclusively online</b> using Platform-approved digital tools.</p>
            <p class="text-body mb-2">5.2 <b>In-person sessions</b>, including sessions conducted at a student’s home or a tutor’s home, are <b>strictly prohibited</b>.</p>
            <p class="text-body mb-2">5.3 ILearn’d Tutors does not assume responsibility or liability for any sessions arranged or conducted outside the Platform.</p>
            <p class="text-body mb-3">5.4 Any attempt to arrange or participate in offline or in-person sessions constitutes a <b>material breach</b> of these Terms and may result in immediate suspension or permanent termination of the account.</p>

            <p class="text-body font-bold mb-1">5.5 PLATFORM COMMUNICATION &amp; EMAIL REQUIREMENTS</p>
            <p class="text-body mb-1">5.5.1 ILearn’d Tutors conducts online sessions using third-party video communication tools, including but not limited to Google Meet.</p>
            <p class="text-body mb-2">5.5.2 Users are strongly encouraged to register and use a valid Google (Gmail) account to ensure seamless access to classes, scheduling, and integrated Platform features.</p>
            <p class="text-body mb-1">5.5.3 While Gmail accounts are recommended for optimal performance and compatibility, users may use alternative email providers. However, ILearn’d Tutors does not guarantee full functionality, compatibility, or user experience when non-Google accounts are used.</p>
            <p class="text-body mb-1">5.5.4 Users are solely responsible for ensuring that their email account and device meet the technical requirements necessary to access and participate in online sessions.</p>
            <p class="text-body mb-1">5.5.5 ILearn’d Tutors shall not be liable for:</p>
            <p class="text-body ml-3 mb-1">(a) failure to join sessions due to incompatible email providers;</p>
            <p class="text-body ml-3 mb-1">(b) technical limitations of third-party platforms; or</p>
            <p class="text-body ml-3 mb-3">(c) user-side configuration issues.</p>

            <p class="text-body font-bold mb-1">5.6. STUDENT–TUTOR VERIFICATION PROCESS</p>
            <p class="text-body mb-1">5.6.1 A student who has enrolled with a tutor may, at the student’s discretion, request a verification call with the selected tutor before scheduling tutoring sessions.</p>
            <p class="text-body mb-1">5.6.2 Where a verification call is requested, the tutor shall be responsible for arranging the verification call using the verification process and template provided by ILearn’d Tutors.</p>
            <p class="text-body mb-1">5.6.3 The tutor shall provide the verification call details, including the applicable meeting link, through the designated Platform process. The verification call details shall be sent to the student’s registered email address.</p>
            <p class="text-body mb-1">5.6.4 ILearn’d Tutors shall issue a verification code to the student’s registered email address as part of the verification process.</p>
            <p class="text-body mb-1">5.6.5 The student shall provide the verification code to the tutor during or in connection with the verification process for the purpose of confirming completion of the verification.</p>
            <p class="text-body mb-1">5.6.6 Tutoring classes may only be scheduled through the Platform after the required verification process has been successfully completed and the verification code has been confirmed.</p>
            <p class="text-body mb-1">5.6.7 Students are responsible for ensuring that they have access to the registered email address associated with their Platform account and for providing the verification code when requested.</p>
            <p class="text-body mb-1">5.6.8 Verification codes are confidential and must not be shared with any person other than the tutor responsible for completing the verification process.</p>
            <p class="text-body">5.6.9 ILearn’d Tutors may suspend or restrict the scheduling of tutoring sessions where the verification process has not been completed or where there are reasonable concerns regarding the authenticity or security of the verification process.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">6. PAYMENT TERMS (STUDENTS)</h2>
            <p class="text-body font-bold mb-1">6.1 Platform-Only Payments</p>
            <p class="text-body mb-3">All payments for tutoring sessions shall be processed <b>exclusively through the Platform</b>. Direct or off-platform payments to tutors are prohibited.</p>

            <p class="text-body font-bold mb-1">6.2 Session Authorization &amp; Payment Processing</p>
            <p class="text-body mb-1">By booking a tutoring session through the Platform, the student authorizes ILearn’d Tutors to securely store and authorize a valid payment method for the applicable session fee.</p>
            <p class="text-body mb-1">The applicable session fee shall only be charged upon successful completion of the session in accordance with Platform rules.</p>
            <p class="text-body mb-1">A session shall be considered successfully completed where:</p>
            <p class="text-body ml-3 mb-1">(a) both tutor and student have attended the session;</p>
            <p class="text-body ml-3 mb-1">(b) the session has been conducted for a reasonable duration; and</p>
            <p class="text-body ml-3 mb-2">(c) the session has been marked as completed by both the tutor and the student through the Platform.</p>
            <p class="text-body mb-2">Where one party fails to confirm session completion, ILearn’d Tutors reserves the right to review session logs, attendance records, participation data, communication history, and other relevant Platform data to determine whether the session qualifies as successfully completed.</p>
            <p class="text-body mb-1">Upon successful session completion and confirmation in accordance with this Clause:</p>
            <p class="text-body ml-3 mb-1">(a) the applicable session fee shall be charged to the student’s authorised payment method; and</p>
            <p class="text-body ml-3 mb-2">(b) payment shall be released to the tutor after deduction of the applicable Platform commission.</p>
            <p class="text-body mb-2">Where a session is cancelled, disputed, incomplete, or flagged for investigation, ILearn’d Tutors may withhold, reverse, delay, or decline to process the applicable charge pending review.</p>
            <p class="text-body mb-3">ILearn’d Tutors reserves the right to make the final determination regarding session completion for payment and dispute resolution purposes.</p>

            <p class="text-body font-bold mb-1">6.3 Cancellations and Charges</p>
            <p class="text-body ml-3 mb-1">(a) Cancellation twenty-four (24) hours or more before the scheduled session — no charge shall apply.</p>
            <p class="text-body ml-3 mb-1">(b) Cancellation two (2) to twenty-four (24) hours before the scheduled session — no charge shall apply.</p>
            <p class="text-body ml-3 mb-1">(c) Cancellation less than two (2) hours before the scheduled session, or student no-show — the Platform may process the applicable session charge or cancellation fee.</p>
            <p class="text-body ml-3 mb-3">(d) Tutor cancellation or tutor no-show — no charge shall apply or any incorrectly processed charge shall be refunded.</p>

            <p class="text-body font-bold mb-1">6.4 Refunds</p>
            <p class="text-body mb-1">Refunds, payment reversals, or cancellation of authorized charges may apply only where one of the following applies:</p>
            <p class="text-body ml-3 mb-1">(a) the tutor failed to attend the session;</p>
            <p class="text-body ml-3 mb-1">(b) a verified Platform-related technical failure prevented the session from taking place;</p>
            <p class="text-body ml-3 mb-1">(c) an incorrect or duplicate charge was processed due to a system error; or</p>
            <p class="text-body ml-3 mb-2">(d) a charge was processed despite a valid cancellation in accordance with Platform rules.</p>
            <p class="text-body mb-2">Refunds or reversals shall not be issued for personal reasons, dissatisfaction after successful session completion, or failure to attend without valid cancellation.</p>
            <p class="text-body">All refunds, reversals, and disputes are subject to the Refund Policy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">7. STUDENT CONDUCT AND PLATFORM USE</h2>
            <p class="text-body mb-2">7.1 Students shall use the Platform respectfully and solely for educational purposes.</p>
            <p class="text-body mb-2">7.2 Students shall not:</p>
            <ul class="text-body list-none p-0 m-0 ml-3">
                <li class="mb-1">(a) share login credentials;</li>
                <li class="mb-1">(b) harass tutors or other users;</li>
                <li>(c) attempt to bypass Platform systems or payment mechanisms.</li>
            </ul>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">8. PAST PAPERS &amp; ACADEMIC CONTENT POLICY</h2>
            <p class="text-body font-bold mb-1">8.1 Grade-Restricted Access</p>
            <p class="text-body mb-2">Students may access Past Papers, marking schemes, and related academic materials <b>only for the academic grade level for which they are registered on the Platform</b>.</p>
            <p class="text-body mb-3">Access to Past Papers of other grades, subjects, or curricula is strictly prohibited unless expressly authorised by the Platform.</p>

            <p class="text-body font-bold mb-1">8.2 Permitted Use</p>
            <p class="text-body mb-1">Past Papers may be used solely for:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-3">
                <li class="mb-1">(a) personal learning and revision;</li>
                <li class="mb-1">(b) tutor-guided practice during sessions;</li>
                <li>(c) assessment and progress tracking within the Platform.</li>
            </ul>

            <p class="text-body font-bold mb-1">8.3 Prohibited Use</p>
            <p class="text-body mb-1">Students shall not:</p>
            <p class="text-body ml-3 mb-1">8.3.1 share, distribute, or circulate Past Papers or marking schemes outside the Platform;</p>
            <p class="text-body ml-3 mb-1">8.3.2 reproduce, publish, sell, or commercially exploit academic materials;</p>
            <p class="text-body ml-3 mb-1">8.3.3 claim ownership or authorship of official examination content;</p>
            <p class="text-body ml-3 mb-1">8.3.4 access or attempt to access materials beyond their registered grade level;</p>
            <p class="text-body ml-3 mb-3">8.3.5 engage in plagiarism or academic dishonesty.</p>

            <p class="text-body font-bold mb-1">8.4 Student Work and Intellectual Property</p>
            <p class="text-body">Student answers and submissions remain the intellectual property of the student and shall not be reused or shared without the student’s (or parent/guardian’s) express consent.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">9. AI FEATURES AND USE OF ARTIFICIAL INTELLIGENCE</h2>
            <p class="text-body font-bold mb-1">9.1 Availability of AI Features</p>
            <p class="text-body mb-1">The Platform may provide AI-enabled tools to support learning and administration, including but not limited to:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-3">
                <li class="mb-1">(a) lesson summaries;</li>
                <li class="mb-1">(b) learning insights;</li>
                <li class="mb-1">(c) performance analytics;</li>
                <li>(d) scheduling or organisational assistance.</li>
            </ul>

            <p class="text-body font-bold mb-1">9.2 Nature of AI Features</p>
            <p class="text-body mb-3">AI Features are <b>assistive only</b> and are intended to support understanding and learning. They do not replace tutors, official curricula, marking schemes, or examination authority guidance.</p>

            <p class="text-body font-bold mb-1">9.3 Limitations and Reliance</p>
            <p class="text-body mb-1">Students acknowledge that:</p>
            <p class="text-body ml-3 mb-1">(a) AI-generated outputs may be incomplete, generalised, or contain inaccuracies;</p>
            <p class="text-body ml-3 mb-1">(b) AI outputs shall not be relied upon as definitive or authoritative academic answers; and</p>
            <p class="text-body ml-3 mb-3">(c) final academic responsibility remains with the student and tutor.</p>

            <p class="text-body font-bold mb-1">9.4 Prohibited Use of AI</p>
            <p class="text-body mb-1">Students shall not:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-3">
                <li class="mb-1">(a) submit AI-generated content as original work without tutor guidance;</li>
                <li class="mb-1">(b) misuse AI tools to circumvent learning or assessment processes;</li>
                <li>(c) attempt to manipulate or abuse AI systems.</li>
            </ul>

            <p class="text-body font-bold mb-1">9.5 No Guarantee</p>
            <p class="text-body mb-3">ILearn’d Tutors does not guarantee the accuracy, completeness, or academic outcomes produced by AI Features.</p>

            <p class="text-body font-bold mb-1">9.6 AI Session Participation, Summaries and Transcript Retention</p>
            <p class="text-body mb-3">Where AI-assisted learning tools are available on the Platform, an AI assistant may be invited or enabled to join a tutoring session by either the tutor or the student participating in the session. The AI assistant may process session audio solely for the purpose of generating automated lesson summaries, organisational notes, learning insights, and transcripts to support revision and learning clarity. Access to full transcripts may depend on the user’s subscription plan.<br/>
                For users without access to AI Premium features, transcripts may remain locked but temporarily retained for a limited period of up to ten (10) days following the session. During this period, users may upgrade their subscription to unlock transcript access where applicable. After the retention period expires, transcripts may be permanently deleted and may no longer be recoverable.
            </p>

            <p class="text-body font-bold mb-1">9.7 AI features, access &amp; data retention</p>
            <p class="text-body mb-1">9.7.1 The Platform may generate AI-assisted outputs based on session data, including but not limited to:</p>
            <p class="text-body ml-3 mb-1">(a) class transcripts;</p>
            <p class="text-body ml-3 mb-1">(b) lesson summaries;</p>
            <p class="text-body ml-3 mb-1">(c) key-point notes;</p>
            <p class="text-body ml-3 mb-1">(d) homework detection and reminders; and</p>
            <p class="text-body ml-3 mb-2">(e) learning insights.</p>

            <p class="text-body mb-2">9.7.2 AI-generated transcripts may be made available to all users for a limited duration following the completion of a session.</p>

            <p class="text-body mb-1">9.7.3 Users subscribed to a Premium Plan shall receive full access to AI-powered features, including:</p>
            <p class="text-body ml-3 mb-1">(a) extended transcript access;</p>
            <p class="text-body ml-3 mb-1">(b) AI-generated summaries and notes;</p>
            <p class="text-body ml-3 mb-1">(c) smart reminders and insights; and</p>
            <p class="text-body ml-3 mb-2">(d) enhanced learning support tools.</p>

            <p class="text-body mb-1">9.7.4 Users on Free Plans:</p>
            <p class="text-body ml-3 mb-1">(a) may have no access to transcripts and AI outputs unless the premium is bought; and</p>
            <p class="text-body ml-3 mb-2">(b) acknowledge that such data may be automatically deleted after a period of ten (10) days from the session date.</p>

            <p class="text-body mb-1">9.7.5 ILearn’d Tutors reserves the right to:</p>
            <p class="text-body ml-3 mb-1">(a) define, modify, or limit AI feature availability based on subscription level;</p>
            <p class="text-body ml-3 mb-1">(b) delete or anonymise AI-generated data after the applicable retention period; and</p>
            <p class="text-body ml-3 mb-2">(c) restrict access to AI features where subscriptions lapse or expire.</p>

            <p class="text-body">9.7.6 AI outputs are assistive only and subject to the limitations set out in the AI Usage, Ethics &amp; Governance Policy.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">10. CHILD PROTECTION AND SAFEGUARDING</h2>
            <p class="text-body mb-1">10.1 ILearn’d Tutors enforces a <b>zero-tolerance policy</b> toward abuse, exploitation, grooming, or inappropriate conduct involving minors.</p>
            <p class="text-body mb-1">10.2 All communication between students and tutors shall occur <b>only through the Platform</b></p>
            <p class="text-body mb-1">10.3 Safeguarding concerns must be reported immediately through the Platform’s safety mechanisms.</p>
            <p class="text-body">10.4 ILearn’d Tutors may suspend accounts and notify relevant authorities where legally required.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">11. DATA PROTECTION AND PRIVACY</h2>
            <p class="text-body mb-1">11.1 Personal data is processed in accordance with the <b>Personal Data Protection Act, No. 9 of 2022 of Sri Lanka (PDPA)</b> and the Privacy Policy.</p>
            <p class="text-body">11.2 Session data and analytics may be retained for up to <b>ninety (90) days</b>, unless a longer period is required by law or dispute resolution.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">12. COMPLAINTS AND DISPUTE RESOLUTION</h2>
            <p class="text-body mb-1">12.1 Complaints should be submitted <b>as soon as reasonably practicable and, in any event, within forty-eight (48) hours</b>, unless circumstances beyond the student’s control prevent timely submission.</p>
            <p class="text-body mb-1">12.2 Complaints shall be handled in accordance with the Platform’s <b>Formal Complaints Procedure</b>.</p>
            <p class="text-body">12.3 Nothing in this Clause limits the right to seek relief through a court of competent jurisdiction.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">13. PLATFORM CHAT &amp; MESSAGING TERMS</h2>
            <p class="text-body font-bold mb-1">13.1 Platform-Only Communication</p>
            <p class="text-body ml-3 mb-1">13.1.1 All communication between users of the ILearn’d Tutors Platform, including but not limited to students, tutors, parents or legal guardians, and institutes, <b>must take place exclusively through the Platform’s in-built chat and messaging system</b>.</p>
            <p class="text-body ml-3 mb-1">13.1.2 The Platform chat system is provided to support:
                <br/>(a) educational communication;
                <br/>(b) lesson coordination and scheduling;
                <br/>(c) safeguarding and child protection;
                <br/>(d) record-keeping and dispute resolution; and
                <br/>(e) Platform compliance, safety, and security.
            </p>
            <p class="text-body ml-3 mb-3">13.1.3 <b>Off-platform communication</b>, including but not limited to WhatsApp, Telegram, SMS, phone calls, email, or social media platforms, is <b>strictly prohibited</b>, particularly where minors are involved.</p>

            <p class="text-body font-bold mb-1">13.2 Acceptable Use of Chat</p>
            <p class="text-body ml-3 mb-1">13.2.1 The chat system shall be used <b>solely for educational, administrative, and Platform-related purposes</b>.</p>
            <p class="text-body ml-3 mb-3">13.2.2 All communication must remain:
                <br/>(a) professional;
                <br/>(b) respectful; and
                <br/>(c) appropriate to the educational context.
            </p>

            <p class="text-body font-bold mb-1">13.3 Prohibited Conduct in Chat</p>
            <p class="text-body mb-2">13.3.1 Users shall not, through the Platform chat system:</p>
            <p class="text-body ml-3 mb-1">(a) share personal contact details, including phone numbers, email addresses, or social media handles;</p>
            <p class="text-body ml-3 mb-1">(b) attempt to move conversations off the Platform;</p>
            <p class="text-body ml-3 mb-1">(c) solicit private tutoring, payments, or arrangements outside the Platform;</p>
            <p class="text-body ml-3 mb-1">(d) engage in harassment, bullying, intimidation, or abusive language;</p>
            <p class="text-body ml-3 mb-1">(e) send inappropriate, sexual, suggestive, or offensive content;</p>
            <p class="text-body ml-3 mb-1">(f) share copyrighted material without authorisation;</p>
            <p class="text-body ml-3 mb-1">(g) misuse personal data or confidential information; or</p>
            <p class="text-body ml-3 mb-3">(h) encourage secrecy, unsupervised interaction, or circumvention of Platform rules.</p>

            <p class="text-body mb-3">13.3.2 Any attempt to use the chat system to bypass Platform safeguards, payments, subscriptions, or monitoring <b>constitutes a material breach</b> of the applicable Terms.</p>

            <p class="text-body font-bold mb-1">13.4 Child Protection &amp; Safeguarding</p>
            <p class="text-body ml-3 mb-1">13.4.1 Where a student is a minor:
                <br/>(a) all chat communication must remain strictly educational;
                <br/>(b) private or personal discussions are prohibited; and
                <br/>(c) grooming behaviour, boundary violations, or emotional manipulation are strictly forbidden.
            </p>
            <p class="text-body ml-3 mb-3">13.4.2 ILearn’d Tutors enforces a <b>zero-tolerance policy</b> for child protection violations occurring through the chat system.</p>

            <p class="text-body font-bold mb-1">13.5 Monitoring, Retention &amp; Data Protection</p>
            <p class="text-body ml-3 mb-1">13.5.1 ILearn’d Tutors may:
                <br/>(a) log, store, and review chat communications;
                <br/>(b) use automated and manual moderation tools; and
                <br/>(c) review chat records for safety, compliance, fraud prevention, and dispute resolution.
            </p>
            <p class="text-body ml-3 mb-1">13.5.2 Chat records may be retained:
                <br/>(a) for up to ninety (90) days for operational purposes; or
                <br/>(b) longer where required for investigations, safeguarding, disputes, or legal compliance.
            </p>
            <p class="text-body ml-3 mb-3">13.5.3 All monitoring and data processing is conducted in accordance with the <b>Personal Data Protection Act, No. 9 of 2022 (Sri Lanka)</b> and the Platform’s Privacy Policy.</p>

            <p class="text-body font-bold mb-1">13.6 AI-Assisted Moderation</p>
            <p class="text-body ml-3 mb-1">13.6.1 The Platform may utilise AI-enabled tools to:
                <br/>(a) detect inappropriate language or safeguarding risks;
                <br/>(b) flag potential policy violations; and
                <br/>(c) assist with moderation and safety alerts.
            </p>
            <p class="text-body ml-3 mb-3">13.6.2 AI systems are <b>assistive only</b> and do not replace human review or decision-making.</p>

            <p class="text-body font-bold mb-1">13.7 Reporting &amp; Enforcement</p>
            <p class="text-body ml-3 mb-1">13.7.1 Users may report chat misuse through:
                <br/>(a) in-chat reporting tools;
                <br/>(b) the Platform’s complaints or contact form; or
                <br/>(c) email to <b>support@ilearndsl.com</b>.
            </p>
            <p class="text-body ml-3 mb-3">13.7.2 Violations of this Clause may result in:
                <br/>(a) warnings;
                <br/>(b) chat restrictions;
                <br/>(c) temporary suspension;
                <br/>(d) permanent account termination;
                <br/>(e) loss of access without refund; and
                <br/>(f) reporting to relevant authorities where required by law.
            </p>

            <p class="text-body font-bold mb-1">13.8 No Liability for Off-Platform Communication</p>
            <p class="text-body ml-3">13.8.1 ILearn’d Tutors shall not be responsible or liable for any communication, interaction, or dispute arising from conversations conducted outside the Platform.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">14. LIMITATION OF LIABILITY</h2>
            <p class="text-body mb-1">14.1 ILearn’d Tutors shall not be liable for:</p>
            <ul class="text-body list-none p-0 m-0 ml-3 mb-2">
                <li class="mb-1">• academic outcomes or examination results;</li>
                <li class="mb-1">• tutor teaching quality;</li>
                <li>• off-Platform interactions.</li>
            </ul>
            <p class="text-body">Liability, where applicable, shall be limited as set out in the ILearn’d Tutors User Agreement.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">15. AMENDMENTS</h2>
            <p class="text-body">Material amendments shall be notified through the Platform and, where required by law, shall require <b>explicit consent</b> before taking effect.</p>
        </div>

        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">16. GOVERNING LAW AND JURISDICTION</h2>
            <p class="text-body">These Terms shall be governed by the laws of the <b>Democratic Socialist Republic of Sri Lanka</b>.<br/>
                All disputes shall be subject to the <b>jurisdiction of the courts of Sri Lanka</b>.
            </p>
        </div>

        <!-- <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">17. CONTACT AND SUPPORT</h2>
            <div class="p-3 border-round bg-faded">
                <p class="text-body font-bold mb-1">ILearn’d Tutors – Legal Team</p>
                <p class="text-body mb-1">Email: support@ilearndsl.com</p>
                <p class="text-body mb-0">Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka</p>
            </div>
        </div> -->
        <div class="policy-section mb-5">
            <h2 class="H5-SemiBold text-primary mb-3">17. CONTACT AND SUPPORT</h2>
            <p class="text-body font-bold mb-2">ILearn’d Tutors – Legal Team</p>
            <p class="text-body mb-1">
                Email: <a href="mailto:support@ilearndsl.com"
                    class="text-primary no-underline">support@ilearndsl.com</a>
            </p>
            <p class="text-body">
                Address: 306/1 Highlevel Road, Colombo 06, Sri Lanka
            </p>
        </div>

        <div class="policy-section">
            <h2 class="H5-SemiBold text-primary mb-3">VERSION CONTROL</h2>
            <div class="overflow-x-auto">
                <table class="w-full border-collapse border border-300 text-body version-table">
                    <thead>
                        <tr class="bg-faded">
                            <th class="border border-300 p-2 text-left font-bold">VERSION</th>
                            <th class="border border-300 p-2 text-left font-bold">DATE</th>
                            <th class="border border-300 p-2 text-left font-bold">DESCRIPTION</th>
                            <th class="border border-300 p-2 text-left font-bold">APPROVED BY</th>
                            <th class="border border-300 p-2 text-center font-bold">SIGNATURE</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="border border-300 p-2">1.0</td>
                            <td class="border border-300 p-2">11/03/2026</td>
                            <td class="border border-300 p-2">Initial Release</td>
                            <td class="border border-300 p-2">Founder</td>
                            <td class="border border-300 p-0 text-center">
                                <img :src="founderSignature" alt="Founder signature"
                                    class="signature-img" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup>
import founderSignature from "@/assets/founder-signature.png";
</script>

<style scoped>
.text-primary { color: #1D97BD !important; }
.bg-faded { background-color: rgba(29, 151, 189, 0.04); }
.text-body {
    font-size: 0.95rem;
    color: #4A5568;
    font-weight: 400;
    line-height: 1.6;
}
.ml-3 { margin-left: 1.5rem; }

/* VERSION CONTROL table */
.version-table th,
.version-table td {
    vertical-align: middle;
    white-space: nowrap;
}
.version-table td:nth-child(3) {
    white-space: normal;
}
.signature-img {
    height: 120px;
    width: auto;
    max-width: 100%;
    display: inline-block;
    vertical-align: middle;
}
</style>
